import { RecordingType, RecordingTypeBadge } from '@carboncopyco/components';
import { useGetDemoQuery } from '@carboncopyco/data';
import {
  EditorNavbar as Navbar,
  useEditorTools,
} from '@carboncopyco/editor-sdk';
import {
  type IDemo,
  type IEditorDraft,
  RecordingTypes,
} from '@carboncopyco/types';
import {
  type IGlobalTab,
  TabGroupName,
  TabType,
} from '@carboncopyco/types/desktop';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { useRef, useState } from 'react';
import { generateGroupId } from '../../../util/tab-util';
import useEditorGroupTab from '../../hooks/useEditorGroupTab';
import { useFlagsFallback } from '../../hooks/useFlagsFallback';
import { useTabFocus } from '../../hooks/useTabFocus';
import useOpenDemoTab from '../../screens/utils/openDemoTab';
import { useUser } from '../../store/context';
import ModalWrapper from '../Modals/ModalWrapper';
import { EditorNavigation } from './EditorNavigation';
import { NavbarOptionsButton } from './NavbarOptionsButton';
import PublishDemoModal from './Publish/PublishDemoModal';
import type { IEditorPageInfo } from './hooks/useEditorPageInfo';

interface EditorNavbarProps {
  pageInfo?: IEditorPageInfo;
  draft?: IEditorDraft;
  publishDiscardDisabled: boolean;
  tab: IGlobalTab;
  onExit: () => void;
}

export const EditorNavbar = ({
  pageInfo,
  draft,
  publishDiscardDisabled,
  tab,
  onExit,
}: EditorNavbarProps) => {
  const publishDemoRef = useRef(null);
  const [publishModalVisible, setPublishModalVisible] = useState(false);
  const [savingDraftInProgress, setSavingDraftInProgress] = useState(false);

  const { closeAll: closeAllModals } = useEditorTools();
  const { trackEvent } = useAnalytics();
  const { updateEditorTabVisibility } = useEditorGroupTab();
  const { activeOrganization } = useUser();
  const openDemoTab = useOpenDemoTab();
  const flags = useFlagsFallback();

  const demo = tab.payload.demo
    ? (tab.payload.demo as IDemo)
    : (tab.payload as IDemo);
  const isLiveEditor = demo?.recordingType === RecordingTypes.overlay || !demo;

  const { data: demoData } = useGetDemoQuery(
    {
      workspaceId: tab.payload.demo.workspaceId,
      organizationId: activeOrganization.id.toString(),
      id: demo?.id.toString() || '',
    },
    { skip: !demo?.id }
  );

  useTabFocus({
    tab,
    onTabIsDeactivated: () => {
      setPublishModalVisible(false);
    },
  });

  const handlePreview = () => {
    if (demoData) {
      const variables = [...(draft?.variables || [])];
      const groupId = generateGroupId(demoData.id, TabGroupName.DemoEditor);
      closeAllModals();

      updateEditorTabVisibility({
        editorTab: tab,
        show: false,
      });

      openDemoTab(
        {
          ...demoData,
          variables,
          playerToken: draft?.playerToken || demoData.playerToken,
        },
        {
          tabTitle: demoData.demoName,
          tabType:
            demoData.recordingType === RecordingTypes.overlay
              ? TabType.PREVIEW_OVERLAY
              : TabType.PREVIEW_DEMO,
          isOpenedFromEditor: true,
          group: groupId
            ? {
                id: groupId,
                name: TabGroupName.DemoPreview,
                isVisibleIfGroupIsActive: true,
                isVisibleIfGroupIsInactive: true,
                showNonRelatedGroupTabsIfActive: false,
                focusOnCreate: true,
                closeGroupOnClose: false,
                mergingOptions: {
                  allowed: true,
                  key: groupId,
                  isDetached: false,
                  index: 0,
                  detachedTitle: demoData.demoName,
                },
              }
            : undefined,
        }
      );

      trackEvent(AnalyticsTrackEventName.DEMO_CLICK_PREVIEW, {
        location: 'demo_editor_page',
        demoId: demo.id,
      });
    }
  };

  return (
    <>
      <Navbar>
        <Navbar.RecordingTypeIcon />
        <Navbar.PageDetails
          title={pageInfo?.title ?? ''}
          description={
            <RecordingTypeBadge
              recordingType={
                demo.recordingType === RecordingTypes.overlay
                  ? RecordingType.Overlay
                  : RecordingType.Clone
              }
            />
          }
        />
        <Navbar.Separator />
        <EditorNavigation
          tab={tab}
          url={pageInfo?.url ?? ''}
        />
        <div className="flex-grow" />
        <Navbar.ButtonGroup>
          {!isLiveEditor && (
            <>
              <Navbar.UndoButton />
              <Navbar.RedoButton />
              <Navbar.ChangesTrackerButton />
            </>
          )}
          <NavbarOptionsButton tab={tab} />
        </Navbar.ButtonGroup>
        <Navbar.Separator />
        <Navbar.ButtonGroup>
          <Navbar.PreviewButton
            onClick={handlePreview}
            disabled={savingDraftInProgress}
          />
          <div ref={publishDemoRef}>
            <Navbar.SaveButton
              disabled={publishDiscardDisabled || publishModalVisible}
              onClick={() => setPublishModalVisible(!publishModalVisible)}
            />
          </div>
        </Navbar.ButtonGroup>
      </Navbar>
      {demo && (
        <ModalWrapper
          isVisible={publishModalVisible}
          onCloseModal={() => setPublishModalVisible(false)}
        >
          <PublishDemoModal
            organizationId={activeOrganization.id.toString()}
            draft={draft}
            buttonRef={publishDemoRef}
            recordingType={demo.recordingType || RecordingTypes.native}
            onClose={() => setPublishModalVisible(false)}
            onExit={onExit}
            saveOpen={() => setPublishModalVisible(true)}
            visible={publishModalVisible}
            demoName={demo.demoName}
            workspaceId={demo.workspaceId}
            demoId={demo.id.toString()}
            workspacesFlag={flags.platformWorkspaces}
            onSavingDraftStarted={() => setSavingDraftInProgress(true)}
            onSavingDraftFinished={() => setSavingDraftInProgress(false)}
          />
        </ModalWrapper>
      )}
    </>
  );
};
